import { useCallback, useMemo, useState } from 'react'

import { FilterCollection } from '@hap/analytics'
import {
  Command,
  CommandCheckItem,
  CommandGroup,
  CommandInput,
  CommandList,
  CommandSpinner,
} from '@hap/components'

import { useDatastore } from '../datastore'
import { CommandSelectAll } from './CommandSelectAll'
import { BaseCommandListProps } from './types'

export function RolesCommandList({
  placeholder = 'Roles',
  selected = [],
  onSelect,
  multiselect,
}: BaseCommandListProps<false, string>) {
  const [query, setQuery] = useState('')
  const { data, isInitialLoading } = useDatastore(FilterCollection.Roles)

  const isSearching = query.trim().length > 0

  const visibleItems = useMemo(() => {
    if (!isSearching) return data
    const normalizedQuery = query.toLowerCase()
    return data?.filter(
      (role) =>
        role.name.toLowerCase().includes(normalizedQuery) ||
        role.meta.description?.toLowerCase().includes(normalizedQuery),
    )
  }, [data, query, isSearching])

  const handleSelectAll = useCallback(
    (newSelection: string[]) => {
      onSelect(newSelection)
    },
    [onSelect],
  )

  const handleToggleItem = useCallback(
    (id: string) => {
      if (multiselect) {
        // Multi-select: toggle the item
        const newSelection = selected.includes(id)
          ? selected.filter((s) => s !== id)
          : [...selected, id]
        onSelect(newSelection)
      } else {
        // Single-select: replace with just this item
        onSelect([id])
      }
    },
    [multiselect, selected, onSelect],
  )

  return isInitialLoading ? (
    <CommandSpinner />
  ) : (
    <Command>
      {(data?.length || 0) > 10 && (
        <CommandInput
          placeholder={placeholder}
          autoFocus
          value={query}
          onValueChange={setQuery}
        />
      )}
      <CommandList className={(data?.length || 0) > 10 ? 'py-1' : ''}>
        <CommandGroup>
          <CommandSelectAll
            visibleItems={visibleItems || []}
            selectedIds={selected}
            onSelect={handleSelectAll}
            query={query}
            multiselect={multiselect}
          />
          {visibleItems?.map((role) => (
            <CommandCheckItem
              key={role.id}
              value={role.id}
              keywords={[role.name]}
              className="group mx-1 grid gap-3"
              selected={selected.includes(role.id)}
              onSelect={() => handleToggleItem(role.id)}
              multiselect={multiselect}
            >
              <div className="grid">
                <span>{role.name}</span>
                <span className="text-muted-foreground text-xs">
                  {role.meta.description}
                </span>
              </div>
            </CommandCheckItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  )
}
